// ═══════════════════════════════════════════════════════════════
// MemoryScopeToggle — Session 49 : Suspendre la mémoire persistante
// Toggle Lock/Unlock + modale explicative premium + tooltip hover.
// Symétrique de ConsolidateModal (narrative ownership mémoire IA).
// ═══════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import { Lock, Unlock, X, EyeOff, Database, Sparkles } from 'lucide-react';
import { toast } from '../ui/Toast';

const EPHEMERAL_AMBER = '#f59e0b';
const EPHEMERAL_BG = 'rgba(245, 158, 11, 0.12)';
const PERSISTENT_BLUE = '#3b82f6';

const SEEN_KEY = (projectId) => `memory_scope_confirm_seen_${projectId}`;

export default function MemoryScopeToggle({
  projectId,
  sessionId,
  currentScope = 'persistent',
  disabled = false,
  onScopeChanged,
}) {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dontAsk, setDontAsk] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimer = useRef(null);

  const isEphemeral = currentScope === 'ephemeral';
  const isDisabled = disabled || !sessionId;

  const toggleScope = async (skipModal = false) => {
    if (isDisabled || submitting) return;

    const next = isEphemeral ? 'persistent' : 'ephemeral';

    // Première fois en ephemeral → demander confirmation (sauf si "Ne plus me demander")
    if (!skipModal && next === 'ephemeral') {
      const seen = localStorage.getItem(SEEN_KEY(projectId));
      if (!seen) {
        setShowModal(true);
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch(`/api/projects/${projectId}/sessions/${sessionId}/memory-scope`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ scope: next }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Erreur toggle scope');
      }

      if (dontAsk && next === 'ephemeral') {
        localStorage.setItem(SEEN_KEY(projectId), '1');
      }

      if (next === 'ephemeral') {
        toast.warning(`Mémoire suspendue — ${data.messages_affected || 0} message(s) retiré(s) de la mémoire projet`);
      } else {
        toast.success(`Mémoire réactivée — indexation en cours (${data.messages_affected || 0} message(s))`);
      }

      setShowModal(false);
      if (onScopeChanged) onScopeChanged(next, data);
    } catch (e) {
      toast.error(e.message || 'Erreur toggle mémoire');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMouseEnter = () => {
    clearTimeout(tooltipTimer.current);
    tooltipTimer.current = setTimeout(() => setShowTooltip(true), 400);
  };
  const handleMouseLeave = () => {
    clearTimeout(tooltipTimer.current);
    setShowTooltip(false);
  };

  const btnColor = isEphemeral ? EPHEMERAL_AMBER : 'var(--text-secondary)';
  const btnBg = isEphemeral ? EPHEMERAL_BG : 'none';
  const IconComp = isEphemeral ? Lock : Unlock;

  const tooltipTitle = isEphemeral
    ? 'Mémoire suspendue — cette session n\'enrichit PAS la mémoire projet'
    : 'Suspendre la mémoire — rendre cette session totalement hors mémoire';

  return (
    <>
      <div
        style={{ position: 'relative', display: 'inline-flex' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          onClick={() => toggleScope(false)}
          disabled={isDisabled || submitting}
          aria-label={tooltipTitle}
          style={{
            background: btnBg,
            border: 'none',
            borderRadius: '6px',
            padding: '6px',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            color: btnColor,
            opacity: isDisabled ? 0.4 : 1,
            flexShrink: 0,
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
          }}
          onMouseEnter={e => { if (!isDisabled) e.currentTarget.style.color = isEphemeral ? EPHEMERAL_AMBER : PERSISTENT_BLUE; }}
          onMouseLeave={e => { e.currentTarget.style.color = btnColor; }}
        >
          <IconComp size={14} />
        </button>

        {/* Tooltip premium multi-lignes */}
        {showTooltip && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              zIndex: 200,
              background: 'var(--bg-elevated, #1a1b2e)',
              border: `1px solid ${isEphemeral ? EPHEMERAL_AMBER : 'var(--border-subtle, #2a2b3f)'}`,
              borderRadius: '8px',
              padding: '10px 12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              minWidth: '260px',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontSize: '11px', fontWeight: 600, color: isEphemeral ? EPHEMERAL_AMBER : 'var(--text-primary, #e5e5ed)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <IconComp size={12} />
              {isEphemeral ? 'Mémoire suspendue' : 'Suspendre la mémoire'}
            </div>
            <div style={{ fontSize: '10.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {isEphemeral ? (
                <>
                  <div>• Aucune indexation sémantique</div>
                  <div>• Aucune injection dans le contexte projet</div>
                  <div>• Aucune participation au résumé long-terme</div>
                  <div style={{ marginTop: '4px', fontStyle: 'italic', color: 'var(--text-muted)' }}>Clic : réactiver la mémoire (ré-indexation rétroactive)</div>
                </>
              ) : (
                <>
                  <div>• Les messages sont indexés sémantiquement</div>
                  <div>• Le contexte long-terme du projet est enrichi</div>
                  <div>• Les messages alimentent le résumé projet</div>
                  <div style={{ marginTop: '4px', fontStyle: 'italic', color: 'var(--text-muted)' }}>Clic : suspendre (les messages sortent de la mémoire)</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modale de confirmation premium (première suspension) */}
      {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}
          onClick={() => !submitting && setShowModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-card, #12131f)',
              border: `1px solid ${EPHEMERAL_AMBER}`,
              borderRadius: '14px',
              padding: '28px',
              maxWidth: '520px',
              width: '100%',
              boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px ${EPHEMERAL_BG}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  background: EPHEMERAL_BG, borderRadius: '10px', padding: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Lock size={20} color={EPHEMERAL_AMBER} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: 'var(--text-primary)' }}>
                    Suspendre la mémoire de cette conversation
                  </h3>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>
                    Session hors mémoire — réversible à tout moment
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                disabled={submitting}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', padding: '4px', display: 'flex',
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'flex-start' }}>
                <EyeOff size={14} color={EPHEMERAL_AMBER} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                  Les messages ne sont <strong style={{ color: 'var(--text-primary)' }}>PAS indexés sémantiquement</strong> dans la mémoire long-terme.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'flex-start' }}>
                <Database size={14} color={EPHEMERAL_AMBER} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Pas d'injection</strong> dans le contexte long-terme du projet.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'flex-start' }}>
                <Sparkles size={14} color={EPHEMERAL_AMBER} style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>Pas de participation</strong> au résumé projet ni à la mémoire trans-projet.
                </div>
              </div>
              <div style={{
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.25)',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                lineHeight: 1.5,
              }}>
                <strong style={{ color: PERSISTENT_BLUE }}>Réversible :</strong> tu peux réactiver la mémoire à tout moment. Les messages seront alors indexés rétroactivement.
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={dontAsk}
                onChange={e => setDontAsk(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Ne plus me demander sur ce projet
            </label>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={submitting}
                style={{
                  background: 'none',
                  border: '1px solid var(--border-subtle, #2a2b3f)',
                  color: 'var(--text-secondary)',
                  padding: '9px 16px',
                  borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => toggleScope(true)}
                disabled={submitting}
                style={{
                  background: EPHEMERAL_AMBER,
                  border: 'none',
                  color: '#0a0a0f',
                  padding: '9px 18px',
                  borderRadius: '8px',
                  cursor: submitting ? 'wait' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Lock size={14} />
                {submitting ? 'Suspension...' : 'Suspendre la mémoire'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
